import type { Milliseconds } from "@superego/global-types";
import { RouteName, toHref } from "@superego/routing";
import { useEffect, useState } from "react";
import { ListBox } from "react-aria-components";
import { FormattedMessage, useIntl } from "react-intl";
import { useGlobalData } from "../../../business-logic/backend/GlobalData.js";
import type SearchState from "../../../business-logic/search/SearchState.js";
import SearchType from "../../../business-logic/search/SearchType.js";
import CollectionUtils from "../../../utils/CollectionUtils.js";
import {
  ConversationSearchResult,
  DocumentSearchResult,
} from "../../design-system/SearchResult/SearchResult.js";
import * as cs from "./SearchModal.css.js";

const searchingIndicatorDelay: Milliseconds = 500;

interface Props {
  searchState: SearchState;
  onNavigateToSearchResult: () => void;
}
export default function SearchResults({
  searchState,
  onNavigateToSearchResult,
}: Props) {
  const intl = useIntl();
  const { collections } = useGlobalData();
  const collectionsById = CollectionUtils.makeByIdMap(collections);

  const [indicator, setIndicator] = useState({
    isSearching: searchState.isSearching,
    revision: 0,
    delayElapsed: false,
  });
  if (indicator.isSearching !== searchState.isSearching) {
    setIndicator({
      isSearching: searchState.isSearching,
      revision: indicator.revision + 1,
      delayElapsed: false,
    });
  }
  const { revision } = indicator;
  useEffect(() => {
    if (searchState.isSearching) {
      const timeoutId = setTimeout(() => {
        // A timer from an earlier searching period cannot reveal this one.
        setIndicator((current) =>
          current.revision === revision
            ? { ...current, delayElapsed: true }
            : current,
        );
      }, searchingIndicatorDelay);
      return () => clearTimeout(timeoutId);
    }
    return;
  }, [searchState.isSearching, revision]);
  const showSearchingIndicator =
    searchState.isSearching && indicator.delayElapsed;

  if (searchState.results === null) {
    return showSearchingIndicator ? (
      <div className={cs.SearchResults.root}>
        <div className={cs.SearchResults.emptyState}>
          <FormattedMessage defaultMessage="Searching..." />
        </div>
      </div>
    ) : null;
  }

  if (searchState.results.length === 0) {
    return (
      <div className={cs.SearchResults.root}>
        <div className={cs.SearchResults.emptyState}>
          {showSearchingIndicator ? (
            <FormattedMessage defaultMessage="Searching..." />
          ) : (
            <FormattedMessage defaultMessage="No results found" />
          )}
        </div>
      </div>
    );
  }

  return (
    <ListBox
      aria-label={intl.formatMessage({ defaultMessage: "Search results" })}
      selectionMode="none"
      className={cs.SearchResults.root}
      onAction={() => {
        onNavigateToSearchResult();
      }}
    >
      {searchState.searchType === SearchType.Documents
        ? searchState.results.map((result) => (
            <DocumentSearchResult
              key={result.match.id}
              result={result}
              collection={collectionsById[result.match.collectionId] ?? null}
              href={toHref({
                name: RouteName.Document,
                collectionId: result.match.collectionId,
                documentId: result.match.id,
              })}
            />
          ))
        : searchState.results.map((result) => (
            <ConversationSearchResult key={result.match.id} result={result} />
          ))}
    </ListBox>
  );
}
